// TAŞI akışı — tek noktadan yönetilen ortak kanca (4 arayüz aynı mantığı kullanır).
// Alt görev sayısı varsa TEK onay sorusu sorulur ve cevap TÜM seçime uygulanır.
import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { tasksApi } from "./api";
import { useAuth } from "./auth";
import { confirmDialog } from "./confirm";
import { tasimaGeriAlBildirimi } from "./moveUndo";
import { sonHedefEkle, sonHedefleriOku } from "./moveRecentTargets";
import { flattenSubs } from "./subtaskTree";

export function useTaskMove({ tasks = [], refresh } = {}) {
  const [istek, setIstek] = useState(null); // {taskIds, subMoves, baslik, bilgi, allowRoot, exclude}
  const [busy, setBusy] = useState(false);
  const kilit = useRef(false);
  const { user } = useAuth() || {};
  const userId = user?.id;

  // Ana görevleri taşı (hedefin içine alt görev olarak)
  const anaGorevleriTasi = useCallback((ids) => {
    const secili = tasks.filter((t) => ids.includes(t.id));
    if (!secili.length) return;
    setIstek({
      taskIds: ids,
      subMoves: [],
      baslik: ids.length > 1 ? `${ids.length} GÖREVİ TAŞI` : "GÖREVİ TAŞI",
      bilgi: "Seçilen görev(ler) hedef görevin içine alt görev olarak taşınır.",
      allowRoot: false,
      exclude: ids,
      recentIds: sonHedefleriOku(userId),
    });
  }, [tasks, userId]);

  // Alt görevleri taşı (aynı görevden seçilenler)
  const altGorevleriTasi = useCallback((taskId, subIds, { allowRoot = true } = {}) => {
    if (!subIds?.length) return;
    setIstek({
      taskIds: [],
      subMoves: [{ task_id: taskId, sub_ids: subIds }],
      baslik: subIds.length > 1 ? `${subIds.length} ALT GÖREVİ TAŞI` : "ALT GÖREVİ TAŞI",
      bilgi: "Hedef görev seçin ya da ana göreve çıkarın.",
      allowRoot,
      exclude: [],
      recentIds: sonHedefleriOku(userId),
    });
  }, [userId]);

  // ANA GÖREVE ÇIKAR — hedef sormadan doğrudan
  const anaGoreveCikar = useCallback(async (taskId, subIds) => {
    if (kilit.current || !subIds?.length) return;
    kilit.current = true;
    try {
      const r = await tasksApi.bulkMove({
        task_ids: [], sub_moves: [{ task_id: taskId, sub_ids: subIds }],
        target_task_id: null, move_children: true,
      });
      tasimaGeriAlBildirimi({ activityId: r.activity_id, mesaj: r.mesaj, refresh });
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Taşınamadı (kod ${e?.response?.status || "ağ"})`);
    } finally {
      kilit.current = false;
    }
  }, [refresh]);

  const kapat = () => setIstek(null);

  // Onay + istek gönderme çekirdeği (hem pencereden hem sürükle-bıraktan).
  const uygula = useCallback(async (mevcut, targetId, targetSubId = null) => {
    let etkilenen = 0;
    let altSayisi = 0;
    const kalanAdaylari = [];
    for (const id of mevcut.taskIds) {
      const t = tasks.find((x) => x.id === id);
      const n = flattenSubs(t?.subtasks || []).length;
      if (n) { etkilenen += 1; altSayisi += n; kalanAdaylari.push(t?.title || "Görev"); }
    }
    for (const m of mevcut.subMoves) {
      const t = tasks.find((x) => x.id === m.task_id);
      for (const sid of m.sub_ids) {
        const node = flattenSubs(t?.subtasks || []).find((s) => s.id === sid);
        const n = flattenSubs(node?.children || []).length;
        if (n) { etkilenen += 1; altSayisi += n; }
      }
    }
    let birlikte = true;
    let kalanBaslik = "";
    if (etkilenen > 0) {
      const varsayilan = kalanAdaylari.length
        ? `«${kalanAdaylari[0]}» (kalan alt görevler)`
        : "";
      const cevap = await confirmDialog({
        title: "ALT GÖREVLER",
        message: `${etkilenen} görevin toplam ${altSayisi} alt görevi var. Alt görevleri de taşıyayım mı?`,
        confirmText: "EVET, BİRLİKTE TAŞI",
        cancelText: "HAYIR, GERİDE BIRAK",
        ...(varsayilan ? {
          extraInput: {
            label: "GERİDE KALANLARIN TOPLANACAĞI GÖREV ADI",
            defaultValue: varsayilan,
            maxLength: 200,
            hint: kalanAdaylari.length > 1
              ? "Çok görev seçildi: yazdığınız metin ÖNEK olur, sonuna görev adı eklenir."
              : "Yalnızca «HAYIR, GERİDE BIRAK» seçilirse kullanılır.",
          },
        } : {}),
      });
      if (typeof cevap === "object" && cevap !== null) {
        birlikte = !!cevap.ok;
        kalanBaslik = (cevap.extra || "").trim();
      } else {
        birlikte = !!cevap;
      }
    }
    kilit.current = true;
    setBusy(true);
    try {
      const r = await tasksApi.bulkMove({
        task_ids: mevcut.taskIds,
        sub_moves: mevcut.subMoves,
        target_task_id: targetId,
        target_sub_id: targetSubId,
        move_children: birlikte,
        remaining_title: birlikte ? null : (kalanBaslik || null),
      });
      if (targetId) sonHedefEkle(userId, targetId);
      tasimaGeriAlBildirimi({ activityId: r.activity_id, mesaj: r.mesaj, refresh });
      refresh?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Taşınamadı (kod ${e?.response?.status || "ağ"})`);
    } finally {
      setBusy(false);
      kilit.current = false;
    }
  }, [tasks, refresh, userId]);

  // FAZ 2B — sürükle-bırak: hedef zaten belli, pencere açılmaz.
  const suruklemeIleTasi = useCallback(({ taskIds = [], subMoves = [], targetId, targetSubId = null }) => {
    if (kilit.current) return;
    if (!taskIds.length && !subMoves.length) return;
    uygula({ taskIds, subMoves }, targetId, targetSubId);
  }, [uygula]);

  const hedefSecildi = useCallback(async (targetId) => {
    if (!istek || kilit.current) return;
    const mevcut = istek;
    setIstek(null); // hedef penceresi kapanır → onay sorusu tek başına görünür
    await uygula(mevcut, targetId);
  }, [istek, uygula]);

  return {
    istek, busy, anaGorevleriTasi, altGorevleriTasi, anaGoreveCikar,
    kapat, hedefSecildi, suruklemeIleTasi,
  };
}
